import type { SvgIconComponent } from '@mui/icons-material';
import {
  AccountBalanceWalletOutlined,
  AirportShuttleOutlined,
  AssignmentOutlined,
  BadgeOutlined,
  BoltOutlined,
  BusinessOutlined,
  CampaignOutlined,
  CategoryOutlined,
  CircleOutlined,
  Co2Outlined,
  DevicesOtherOutlined,
  FactCheckOutlined,
  GpsFixedOutlined,
  GroupOutlined,
  HubOutlined,
  Inventory2Outlined,
  InventoryOutlined,
  LayersOutlined,
  LocalShippingOutlined,
  MenuBookOutlined,
  PendingActionsOutlined,
  PlaceOutlined,
  PlaylistAddCheckOutlined,
  ReceiptLongOutlined,
  RecyclingOutlined,
  RouteOutlined,
  SensorsOutlined,
  ShoppingCartOutlined,
  SpaceDashboardOutlined,
  StorefrontOutlined,
  VerifiedUserOutlined,
  VpnKeyOutlined,
  WarehouseOutlined,
} from '@mui/icons-material';

/**
 * One outlined, monochrome icon per admin destination, keyed by route path.
 * The sidebar and each page header use the same icon so a page is recognisable
 * from both places.
 */
export const NAV_ICONS: Record<string, SvgIconComponent> = {
  '/': SpaceDashboardOutlined,
  '/organizations': BusinessOutlined,
  '/users': GroupOutlined,
  '/api-keys': VpnKeyOutlined,
  '/notifications/send': CampaignOutlined,
  '/approvals': FactCheckOutlined,
  '/verification/batches': VerifiedUserOutlined,
  '/finance': AccountBalanceWalletOutlined,
  '/operations/waste-movers': LocalShippingOutlined,
  '/operations/waste-outlets': StorefrontOutlined,
  '/operations/recyclers': RecyclingOutlined,
  '/operations/supplier-links': HubOutlined,
  '/operations/facility-links': WarehouseOutlined,
  '/pickup_orders': AssignmentOutlined,
  '/deliveries/aggregations': LayersOutlined,
  '/pickup_orders/batch-complete': PlaylistAddCheckOutlined,
  '/pre_orders': PendingActionsOutlined,
  '/orders': ShoppingCartOutlined,
  '/invoices': ReceiptLongOutlined,
  '/transportation/fleet': AirportShuttleOutlined,
  '/transportation/drivers': BadgeOutlined,
  '/deliveries': RouteOutlined,
  '/truck_tracking': GpsFixedOutlined,
  '/recycling/waste-types': CategoryOutlined,
  '/recycling/materials': InventoryOutlined,
  '/containers/list': Inventory2Outlined,
  '/containers/sites': PlaceOutlined,
  '/devices': SensorsOutlined,
  '/devices/types': DevicesOtherOutlined,
  '/esg/mrv-methodologies': MenuBookOutlined,
  '/esg/emission-factors': Co2Outlined,
  '/esg/utility-emission-factors': BoltOutlined,
};

export function navIcon(path: string): SvgIconComponent {
  return NAV_ICONS[path] ?? CircleOutlined;
}

/**
 * The nav path a route belongs to: the longest nav path that equals the route
 * or is a parent of it, so detail pages (`/operations/recyclers/:id`,
 * `/pickup_orders/create`) highlight their list entry. Routes outside any nav
 * path fall back to the closest section alias.
 */
const ROUTE_ALIASES: Array<[prefix: string, navPath: string]> = [['/verification/', '/verification/batches']];

export function activeNavPath(pathname: string, navPaths: readonly string[]): string | null {
  let best: string | null = null;
  for (const path of navPaths) {
    const matches = path === '/' ? pathname === '/' : pathname === path || pathname.startsWith(`${path}/`);
    if (matches && (best === null || path.length > best.length)) best = path;
  }
  if (best) return best;
  const alias = ROUTE_ALIASES.find(([prefix]) => pathname.startsWith(prefix));
  return alias && navPaths.includes(alias[1]) ? alias[1] : null;
}
